import React from 'react'
import useStore from 'state/knovStore'
import styles from 'components/quests/new-quest-button.module.scss'
import cn from 'classnames'
import { Circle, Pen } from 'lucide-react'
import questModel from 'components/quests/questModel'
import PublicIcon from 'components/shared/icons/PublicIcon'
import MusicIcon from 'components/shared/icons/MusicIcon'
import PrivateIcon from 'components/shared/icons/PrivateIcon'
import TeamIcon from 'components/users/TeamIcon'
import LinkIcon from 'components/shared/icons/LinkIcon'
import { useAvailableTeams } from 'components/filters/useStreamFilters'
import { isOrdinalsMintEnabled } from 'components/quests/mintMediaThreadPolicy'
import { isRadioStreamFilter } from 'components/panels/feedNewItemsEligibility'

export default function NewQuestButtonUIMain(props) {
    const leftBtnStyles = props.showDropdown ? styles.leftBtn : null
    const fullWidthStyles = props.fullWidth ? styles.fullWidth : null
    const fullWidthBtnStyles = props.fullWidth ? styles.fullWidthBtn : null
    const neutralBtnStyles = props.neutral ? styles.neutralBtn : null

    // Use resolved perms from the wrapper when available; it may include fetched stream metadata.
    const perms = props.perms ?? (props.filter ? questModel.permsFromFilter(props.filter) : null)

    // Get teams from store to find the team object
    const teams = useAvailableTeams(gon.currentUser)
    const resolvedTestId = props['data-testid'] || props.testId

    // The ordinals streams swap the composer CTA for the mint-media experience.
    const isOrdinalsMint = isOrdinalsMintEnabled(props.filter)
    // The music channel posts songs: its new-thread button says so.
    const isNewMusic = isRadioStreamFilter(props.filter) && props.label !== 'Join'
    const label = isOrdinalsMint ? 'Mint' : isNewMusic ? 'New Music' : props.label

    // Render the appropriate icon based on permissions
    const renderIcon = () => {
        // Matches the ordinals stream header / tile controls, which mark ordinals
        // with lucide's plain circle.
        if (isOrdinalsMint) {
            return <Circle className={styles.ordinalsMintIcon} strokeWidth={2.2} />
        }

        // New Music posts a public song: an orange music note, not the globe.
        if (isNewMusic) {
            return <MusicIcon contextStyles={cn(styles.filterIcon, styles.publicIcon)} />
        }

        if (!perms) return <PrivateIcon contextStyles={cn(styles.filterIcon, styles.privateIcon)} />

        // Check for links filter (shows as private)
        if (props.filter?.link) {
            return <LinkIcon contextStyles={cn(styles.filterIcon)} />
        }

        // Check for team filter
        if (perms.team_id) {
            const team = perms.team || teams?.find(t => t.id === perms.team_id)
            if (team) {
                return (
                    <TeamIcon team={team} contextStyles={cn(styles.filterIcon, styles.teamIcon)} />
                )
            }
            // Team metadata may not be hydrated yet; still treat as stream context.
            return (
                <div className={cn(styles.filterIcon, styles.fontIcon)}>
                    <i className="fa fa-users" />
                </div>
            )
        }

        // Check for specific public streams or public permission (including ordinals)
        if (
            props.filter?.treechat ||
            props.filter?.hodlocker ||
            props.filter?.twetch ||
            props.filter?.ordinals ||
            perms.public
        ) {
            return <PublicIcon contextStyles={cn(styles.filterIcon, styles.publicIcon)} />
        }

        // Default to private icon (includes starred, history, notifications, etc.)
        return <PrivateIcon contextStyles={cn(styles.filterIcon, styles.privateIcon)} />
    }

    return (
        <div className={cn(styles.newThreadComp, fullWidthStyles, props.contextStyles)}>
            <div
                className={cn(
                    styles.streamBtn,
                    leftBtnStyles,
                    fullWidthBtnStyles,
                    neutralBtnStyles,
                    isOrdinalsMint && styles.ordinalsMintBtn,
                )}
                onClick={props.onClick}
                data-testid={resolvedTestId}
            >
                {props.filter && renderIcon()}

                {props.showIcon && <Pen className={cn(styles.penIcon)} />}

                {props.showLabel && <span className={styles.label}>{label}</span>}
            </div>

            {props.showDropdown && (
                <div
                    className={cn(styles.streamBtn, styles.rightBtn)}
                    onClick={() => props.setShowFilterOptions(!props.showFilterOptions)}
                >
                    {props.showFilterOptions ? (
                        <i className="fa fa-chevron-up" />
                    ) : (
                        <i className="fa fa-chevron-down" />
                    )}
                </div>
            )}
        </div>
    )
}
